import { Button, Input, Label } from '@lensora/ui'
import * as React from 'react'

import { NO_COMPETITORS_TEXT } from '../useOnboardingMachine'

import { FaviconImg } from '@/components/dashboard/FaviconImg'

function guessDomain(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9.]/g, '') + (name.includes('.') ? '' : '.com')
}

interface CompetitorConfirmTurnProps {
  resolved: boolean
  summary?: string | undefined
  names: string[]
  saving: boolean
  onConfirm: (names: string[]) => void
  onSkip: () => void
  // Lifted to OnboardingChat so the persistent composer bar's free-text
  // input can add a competitor here too, same lifted-state pattern as
  // PromptsTurn's "Add your own" input.
  composerExtras: string[]
}

export function CompetitorConfirmTurn({ resolved, summary, names, saving, onConfirm, onSkip, composerExtras }: CompetitorConfirmTurnProps) {
  const capped = React.useMemo(() => names.slice(0, 6), [names])
  const [checkedSet, setCheckedSet] = React.useState<Set<string>>(() => new Set(capped))
  const [extras, setExtras] = React.useState<string[]>([])
  const [customInput, setCustomInput] = React.useState('')

  React.useEffect(() => {
    setExtras((prev) => {
      const added = composerExtras.filter((n) => !prev.includes(n))
      if (added.length === 0) return prev
      return [...prev, ...added]
    })
    setCheckedSet((prev) => {
      const next = new Set(prev)
      composerExtras.forEach((n) => next.add(n))
      return next
    })
  }, [composerExtras])

  if (resolved) {
    return <p className="text-sm text-text-muted">{summary}</p>
  }

  function toggle(name: string) {
    setCheckedSet((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  function addCustom() {
    const trimmed = customInput.trim()
    if (!trimmed) return
    setExtras((prev) => [...prev, trimmed])
    setCheckedSet((prev) => new Set(prev).add(trimmed))
    setCustomInput('')
  }

  const allNames = [...capped, ...extras]
  const checkedCount = allNames.filter((n) => checkedSet.has(n)).length

  if (capped.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-text-muted">{NO_COMPETITORS_TEXT}</p>
        {extras.length > 0 && (
          <ul className="space-y-1 text-sm text-text">
            {extras.map((name) => (
              <li key={name} className="flex items-center gap-2">
                <FaviconImg domain={guessDomain(name)} name={name} size={16} />
                {name}
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2">
          <Label htmlFor="onboarding-add-competitor" className="sr-only">Add a competitor</Label>
          <Input
            id="onboarding-add-competitor"
            placeholder="Add a competitor…"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom() } }}
          />
          {customInput.trim() && (
            <Button type="button" size="sm" variant="outline" onClick={addCustom}>+ Add</Button>
          )}
        </div>
        <Button size="sm" disabled={saving} onClick={() => onConfirm(extras)}>
          {saving ? 'Saving…' : 'Continue →'}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        {allNames.map((name) => {
          const checked = checkedSet.has(name)
          return (
            <label
              key={name}
              className="flex cursor-pointer items-center gap-2.5 rounded-lg border border-border bg-surface px-3 py-2 text-sm transition-colors hover:border-brand-accent/40"
              onClick={() => toggle(name)}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(name)}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 rounded border-border accent-brand-accent"
              />
              <FaviconImg domain={guessDomain(name)} name={name} size={16} />
              <span className="flex-1 text-text">{name}</span>
            </label>
          )
        })}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="onboarding-add-competitor" className="text-[11px] font-semibold uppercase tracking-wider">
          Add another
        </Label>
        <div className="flex gap-2">
          <Input
            id="onboarding-add-competitor"
            placeholder="Add a competitor…"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom() } }}
          />
          {customInput.trim() && (
            <Button type="button" size="sm" variant="outline" onClick={addCustom}>+ Add</Button>
          )}
        </div>
      </div>

      <div className="flex gap-2.5">
        <Button size="sm" disabled={saving} onClick={() => onConfirm(allNames.filter((n) => checkedSet.has(n)))}>
          {saving ? 'Adding…' : `Add ${checkedCount} competitor${checkedCount === 1 ? '' : 's'} →`}
        </Button>
        <button type="button" onClick={onSkip} className="text-sm text-text-muted hover:text-text">
          Skip for now
        </button>
      </div>
    </div>
  )
}
